import React, { useState, useContext, ChangeEvent, useMemo } from "react";
import { Report, Page } from "powerbi-client";
import { Report as PowerBiReport } from "powerbi-report-component";

import { Typography, Button, Paper, Card, MenuItem } from "@mui/material";
import { Skeleton } from "@mui/material";
import { Dropdown } from "components/Dropdown";
import CardBody from "components/Card/CardBody";

import useDumbPageTitle from "hooks/useDumbPageTitle";
import {
  useGetPowerBiConfig,
  ReportConfig
} from "hooks/fleet-manager/useGetPowerBiConfig";
import { useTrackPageView } from "hooks/useTrackPageView";

import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";

import { styled } from "@mui/material/styles";

const PAGE_TITLE = "Fleet Manager";

export function FleetManagerPage(): JSX.Element {
  useDumbPageTitle(PAGE_TITLE);
  useTrackPageView(PAGE_TITLE);

  return (
    <ReportCardNoMargin>
      <ReportCardBody>
        <PowerBIReport />
      </ReportCardBody>
    </ReportCardNoMargin>
  );
}

const ReportCardNoMargin = styled(Card)`
  margin-top: 0;
  margin-bottom: 0;
  min-height: 100%;
  @media print {
    -webkit-print-color-adjust: exact;
  }
`;

const ReportCardBody = styled(CardBody)`
  min-height: calc(100vh - 120px);
`;

const CardHeader = styled(Typography)`
  font-size: 18px;
  color: #555555;
`;

function PowerBIReport(): JSX.Element {
  const [report, setReport] = useState<Report>();
  const [reportId, setReportId] = useState<string>();

  const [filtersEnabled, setFiltersEnabled] = useState<boolean>(false);
  const [refreshDisabled, setRefreshDisabled] = useState<boolean>(false);
  // const [editEnabled, setEditEnabled] = useState<boolean>(false);

  const [reportPages, setReportPages] = useState<Page[]>([]);
  const [activePage, setActivePage] = useState<string>("");

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const { loading, error, embedConfig } = useGetPowerBiConfig();

  const reportConfigs = useMemo<ReportConfig[]>(
    () => embedConfig?.reportConfigs ?? [],
    [embedConfig?.reportConfigs]
  );

  const reportConfig = useMemo(() => {
    const currentReportId = reportId ?? embedConfig?.defaultReportId;
    return embedConfig?.reportConfigs?.find(
      ({ reportId }) => reportId === currentReportId
    );
  }, [embedConfig?.defaultReportId, embedConfig?.reportConfigs, reportId]);

  const reportError =
    error || !embedConfig?.reportConfigs || !embedConfig?.embedToken;

  const extraSettings = {
    hideErrors: false,
    panes: {
      filters: { expanded: false, visible: false },
      pageNavigation: { visible: false, position: 1 }
    }
  };

  const reportStyle = {
    height: "calc(100vh - 240px)",
    width: "100%"
  };

  const onLoadReport = (report: Report): void => {
    if (report) {
      report.iframe.style.borderWidth = "0px";
      setReport(report);

      report.getPages().then(pages => {
        report.setPage(pages[0].name);

        setActivePage(pages[0].name);
        setReportPages(pages);
      });
    }
  };

  const onSetFullscreen = (): void => {
    if (report) {
      report.fullscreen();
    }
  };

  // Disabled View Mode
  // const onSetViewmode = (): void => {
  //   if (report) {
  //     report
  //       .switchMode(editEnabled ? "View" : "Edit")
  //       .then(() => setEditEnabled(editEnabled => !editEnabled));
  //   }
  // };

  const onViewFilters = (): void => {
    if (report) {
      const reportFilterSettings = {
        ...report.config.settings,
        panes: {
          ...report.config.settings?.panes,
          filters: { expanded: !filtersEnabled, visible: !filtersEnabled }
        }
      };

      report
        .updateSettings(reportFilterSettings)
        .then(() => setFiltersEnabled(filtersEnabled => !filtersEnabled));
    }
  };

  const onTriggerRefresh = (): void => {
    if (report) {
      setRefreshDisabled(true);

      report
        .refresh()
        .then(() => {
          addSnackbarToQueue({
            message: "Report data refreshed.",
            severity: "success"
          });
        })
        .catch(() => {
          addSnackbarToQueue({
            message: "Report data failed to refresh.",
            severity: "error"
          });
        })
        .finally(() => {
          setTimeout(() => setRefreshDisabled(false), 15000);
        });
    }
  };

  const onChangeReportPage = (page: string): void => {
    if (report) {
      report.setPage(page).then(() => setActivePage(page));
    }
  };

  const onChangeReport = (newReportId: string): void => {
    if (report?.element && report?.service) {
      const newReportConfig = reportConfigs.find(
        ({ reportId }) => reportId === newReportId
      );

      const reportFilterSettings = {
        ...report.config.settings,
        panes: {
          ...report.config.settings?.panes,
          filters: { expanded: filtersEnabled, visible: filtersEnabled }
        }
      };

      if (newReportConfig) {
        const newEmbedConfig = {
          accessToken: embedConfig?.embedToken,
          tokenType: 1, // 1 = "Embed"
          type: "report",
          embedUrl: newReportConfig.embedUrl,
          id: newReportConfig.reportId,
          datasetIds: [newReportConfig.datasetId],
          settings: reportFilterSettings
        };

        report.service.embed(report.element, newEmbedConfig);
        setReportId(newReportConfig.reportId);
      }
    }
  };

  return (
    <>
      <CardHeader>{reportConfig?.reportName}</CardHeader>
      <Header>
        <DropdownGroup>
          <StyledDropdown
            id="reports"
            name="reports"
            data-qa="PBI-Select-Report-Dropdown"
            label="Report"
            size="small"
            disabled={!reportConfigs.length}
            value={reportConfig?.reportId ?? ""}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChangeReport(event.target.value)
            }
            error={error}
            helperText={error ? "Reports failed to load" : ""}
          >
            {reportConfigs.map(({ reportName, reportId }) => (
              <MenuItem key={reportId} value={reportId}>
                {reportName}
              </MenuItem>
            ))}
          </StyledDropdown>
          <StyledDropdown
            id="pages"
            name="pages"
            data-qa="PBI-Select-Report-Page-Dropdown"
            label="Report Page"
            size="small"
            disabled={!report}
            value={activePage}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChangeReportPage(event.target.value)
            }
            error={error}
            helperText={error ? "Report failed to load" : ""}
          >
            {reportPages.map(({ displayName, name }) => (
              <MenuItem key={displayName} value={name}>
                {displayName}
              </MenuItem>
            ))}
          </StyledDropdown>
        </DropdownGroup>
        <ButtonGroup>
          <Button
            onClick={onSetFullscreen}
            variant="outlined"
            color="primary"
            disabled={!report}
          >
            Fullscreen
          </Button>
          <Button
            onClick={onViewFilters}
            variant="outlined"
            color="primary"
            disabled={!report}
          >
            {`${filtersEnabled ? "Hide" : "Show"} Filters`}
          </Button>
          {/* Disabled View Mode
      <ButtonWithMargin
        onClick={onSetViewmode}
        variant="outlined"
        color="primary"
        disabled={!report}
      >
        {`${editEnabled ? "View" : "Edit"} Mode`}
      </ButtonWithMargin> */}
          <Button
            variant="outlined"
            color="primary"
            onClick={() => report && report.print()}
            disabled={!report}
          >
            Print
          </Button>
          <Button
            onClick={onTriggerRefresh}
            variant="contained"
            color="primary"
            disabled={!report || refreshDisabled}
          >
            Refresh Data
          </Button>
        </ButtonGroup>
      </Header>
      <Paper>
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={680} />
        ) : reportError ? (
          <Typography align="center">Failed to load content</Typography>
        ) : (
          <PowerBiReport
            embedUrl={reportConfig?.embedUrl ?? ""}
            embedId={reportConfig?.reportId ?? ""}
            accessToken={embedConfig.embedToken}
            extraSettings={extraSettings}
            tokenType="Embed"
            pageName={reportConfig?.reportName ?? ""}
            reportMode="View"
            style={reportStyle}
            permissions="Read"
            onLoad={onLoadReport}
            /*
             * Additional function props:
             * onRender
             * onError
             * onButtonClicked
             * onSelectData
             * onPageChange
             * onCommandTriggered
             * onSave
             */
          />
        )}
      </Paper>
    </>
  );
}

const Header = styled("div")`
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1)};
  flex-wrap: wrap;
`;

const DropdownGroup = styled("div")`
  display: flex;
  gap: ${props => props.theme.spacing(1)};
`;

const ButtonGroup = styled("div")`
  display: flex;
  gap: ${props => props.theme.spacing(1)};
  flex-wrap: wrap;
  justify-content: flex-end;
  flex-grow: 1;
`;

const StyledDropdown = styled(Dropdown)`
  min-width: 200px;
  text-align: left;
`;
